import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Input} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { HttpResponse } from "@angular/common/http";
import { Subscription, Observable, BehaviorSubject } from "rxjs";
import { tap, filter, finalize, switchMap, mergeMap } from "rxjs/operators";
import { UtilityService, ProcessedMessageResponse } from '../../../../admin/services/utility.service';
import { EDiscoveryService } from "../../../../admin/services/eDiscovery.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { WorkflowData, ApplicationPermitResult } from "../../../../applications/models/applications.models";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { parseFileNameFromHeaders } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { RemindersService } from "../../../../admin/services/reminders.service";


@Component({
    selector: 'utility-widget',
    templateUrl: './utility-widget.component.html',
    styleUrls: ['./utility-widget.component.scss'],
    standalone: false
})
export class UtilityWidgetComponent implements OnInit, OnDestroy {
    @Input() widgetTitle: string;


    agingDays: number = 0;
    applicationNumber = "";
    eDiscoveryResults: string = "";
    eDiscoveryRequest: string;
    eDiscoveryTaskResults: string = "";
    messageState = "";
    messageDate = "";
    messageNotifications = "";
    messageId: string = "";
    messageResult: ProcessedMessageResponse;
    processingMessage: boolean = false;
    reminderResult: string = "";

    get messageStatus(): string {
        let status = "";
        if (this.processingMessage) {
            status = (this.messageResult != null)
                ? this.messageResult.message
                : "In Process";
        }
        return status;
    }

    stratigraphicPermitId: string;

    generatingPermit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    get isGenerating(): boolean {
        return this.generatingPermit.value;
    }

    changeStateNumber = "";
    @ViewChild('downloadLink', { static: true }) public downloadLink: ElementRef;
    subscription = new Subscription();

    constructor(
        private router: Router,
        public readonly utilitySrv: UtilityService,
        public readonly eDiscoveryService: EDiscoveryService,
        public readonly docService: DocumentManagementService,
        private readonly _loader: LoaderService,
        private readonly _appService: ApplicationsService,
        private readonly _dialog: DynamicDialogService,
        private readonly _reminders: RemindersService
    ) { }

    ngOnInit() {
        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscription.unsubscribe();
        });
    }

    ngOnDestroy() {
        if (this.subscription)
            this.subscription.unsubscribe();
    }

    changeApplicationNumber(): void {
        this.utilitySrv.get(this.applicationNumber, true).subscribe(result => {
            this.messageDate = result;
        });
    }

    changeState(): void {
        this.utilitySrv.get(this.changeStateNumber, false).subscribe(result => {
            this.messageState = result;
        });
    }

    deleteNotifications(): void {
        const deleteObs$ = this.utilitySrv.deleteNotifications(this.agingDays).pipe(
            tap((deleted) => {
                this.messageNotifications = `${deleted} notifications deleted`;
            }), finalize(() => {
                this.subscription.add(deleteObs$);
            })
        ).subscribe(() => {
        }, (error) => {
            console.error(error);
            this.messageNotifications = error.error;
        });
    }

    processDocument(): void {
        this.messageResult = null;
        this.processingMessage = true;
        const processObs$ = this.utilitySrv.processMessage(this.messageId)
            .pipe(tap((result: ProcessedMessageResponse) => {
                this.messageResult = result;
            })).subscribe(() => {
                setTimeout(() => {
                    this.processingMessage = false;
                    this.messageResult = null;
                    this.messageId = "";
                }, 20000);
            },
                (error) => { console.error(error); });
        this.subscription.add(processObs$);
    }
        

    runEDiscovery() : void {
        this.eDiscoveryTaskResults = "";
        const taskObs$ = this.eDiscoveryService.simulateEDiscoveryTask().pipe()
            .subscribe((results) => {
                this.eDiscoveryTaskResults = results;
            },
                (error) => {
                    console.error(error);
                    this.eDiscoveryTaskResults = error.error;
                });
        this.subscription.add(taskObs$);
    }

    runReminders(): void {
        const reminder$ = this._reminders.generateAllActiveReminders().pipe()
            .subscribe(
                () => {
                    this.reminderResult = "Success";
                    this._dialog.showSuccessDialog();
                },
                (error) => {
                    this.reminderResult = error.message;
                    this._dialog.showError();
                }
        );
        this.subscription.add(reminder$);
    }

    testGenerate(): void {
        if (this.eDiscoveryRequest && this.eDiscoveryRequest.length > 0) {
            this.eDiscoveryResults = "";
            const docObs$ = this.eDiscoveryService.testGenerateEDiscovery(this.eDiscoveryRequest).pipe()
                .subscribe((data) => {
                    this.eDiscoveryResults = data;
                },
                (error) => {
                    this.eDiscoveryResults = error.error;
                    });
            this.subscription.add(docObs$);
        } else {
            this.eDiscoveryResults = "No Request ID";
        }
    }

    testPermit(): void {
        if (this.stratigraphicPermitId != null) {
            this._loader.toggleLoader(true);
            let obs$: Observable<HttpResponse<Blob>> = this._appService.getApplicationByApplicationNumber(this.stratigraphicPermitId)
                .pipe(
                    switchMap((app: WorkflowData) => {
                        return this._appService.generatePermit(app, true)
                    }),
                    switchMap((resp: ApplicationPermitResult) => {
                        return this.docService.downloadFromQueue(resp.queuedDocumentId);
                    })
            );
            let subs$: Subscription = obs$.subscribe(
                ((data: HttpResponse<Blob>) => {
                    this._loader.toggleLoader(false);
                    if (data != null) {
                        const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                        const blob = new Blob([data.body], { type: data.body.type });
						const blobUri = window.URL.createObjectURL(blob);
						const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
						anchor.href = blobUri;
						anchor.download = fileName;
						anchor.click();
                        this._dialog.showSuccessDialog();
                    }
                }),
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this._dialog.showError();
                }
            )
        }
    }
}
